import React, { useState, useEffect } from 'react';
import '../Settings/styles.css';
import {
  Card,
  Upload,
  Button,
  Steps,
  Table,
  Select,
  message,
  Alert,
  Space,
  Typography,
  Tag,
  Divider,
  List,
  Tabs,
  Modal,
  Descriptions,
  Spin,
} from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  HistoryOutlined,
  EyeOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getToken } from '../../utils/auth';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Step } = Steps;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

interface SystemField {
  label: string;
  required: boolean;
  enum?: string[];
}

interface ParseResult {
  headers: string[];
  auto_mapping: Record<string, string>;
  system_fields: Record<string, SystemField>;
  total_rows: number;
  preview_data: any[];
}

interface DuplicateInfo {
  is_duplicate: boolean;
  duplicate_type: 'file' | 'database' | 'company_name' | 'file_company_name';  // 文件内重复、数据库重复、公司名重复、文件内公司名重复
  is_company?: boolean;
  phone: string;
  new_contact_name: string;
  // 文件内重复特有
  first_occurrence_row?: number;
  // 数据库/公司名重复特有
  existing_lead_id?: number;
  existing_lead_number?: string;
  existing_contact_name?: string;
  existing_status?: string;
  existing_last_follow_time?: string;
  // 公司名重复特有
  existing_phone?: string;
  existing_description?: string;
  // 文件内公司名重复特有
  all_phones?: string[];
  other_phones?: string[];
}

interface ValidationResult {
  is_valid: boolean;
  total_rows: number;
  error_count: number;
  duplicate_count: number;
  company_duplicate_count: number;
  file_duplicate_count?: number;  // 文件内重复数量
  errors: string[];
  preview_data: Array<{
    row_num: number;
    data: any;
    has_error: boolean;
    errors: string[];
    duplicate_info?: DuplicateInfo;
  }>;
}

interface ImportResult {
  success: boolean;
  total_rows: number;
  success_count: number;
  skip_count: number;
  fail_count: number;
  duplicate_data: Array<{
    row_num: number;
    phone: string;
    contact_name: string;
    existing_lead_id: number;
    existing_contact_name: string;
    existing_status: string;
  }>;
  errors: string[];
  message: string;
}

interface ImportHistory {
  id: number;
  original_filename: string;
  file_size: number;
  has_file: boolean;
  imported_by: number;
  importer_name: string;
  import_time: string;
  status: string;
  total_rows: number;
  success_count: number;
  skip_count: number;
  fail_count: number;
  created_at: string;
}

interface ImportHistoryDetail extends ImportHistory {
  field_mapping: Record<string, string>;
  error_details: string[];
  duplicate_details: Array<{
    row_num: number;
    phone: string;
    contact_name: string;
    existing_lead_id: number;
    existing_contact_name: string;
    existing_status: string;
  }>;
  updated_at: string;
}

const LeadImport: React.FC = () => {
  const [activeTab, setActiveTab] = useState('import');
  const [current, setCurrent] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  
  // 重复数据处理决策：{row_num: "skip" | "add_follow" | "create_new"}
  const [duplicateActions, setDuplicateActions] = useState<Record<number, string>>({});

  // 导入历史相关状态
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState<ImportHistory[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(20);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentDetail, setCurrentDetail] = useState<ImportHistoryDetail | null>(null);

  // 步骤1: 上传文件并解析
  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    
    setLoading(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', file as File);

      const response = await fetch('/api/v1/leads/import/parse', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '解析失败');
      }

      const result: ParseResult = await response.json();
      setParseResult(result);
      setFieldMapping(result.auto_mapping);
      setCurrent(1);
      message.success('文件解析成功');
      onSuccess?.(result);
    } catch (error: any) {
      message.error(error.message || '文件解析失败');
      onError?.(error);
    } finally {
      setLoading(false);
    }
  };

  const uploadProps: UploadProps = {
    accept: '.xlsx,.xls',
    maxCount: 1,
    fileList,
    customRequest: handleUpload,
    onChange: ({ fileList }) => setFileList(fileList),
    onRemove: () => {
      setParseResult(null);
      setFieldMapping({});
      setValidationResult(null);
      setCurrent(0);
    },
  };

  // 步骤2: 验证数据
  const handleValidate = async () => {
    if (!fileList[0] || !parseResult) return;

    setLoading(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', fileList[0].originFileObj as File);
      formData.append('mapping', JSON.stringify(fieldMapping));

      const response = await fetch('/api/v1/leads/import/validate', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '验证失败');
      }

      const result: ValidationResult = await response.json();
      setValidationResult(result);

      // 初始化重复数据的默认决策
      const defaultActions: Record<number, string> = {};
      result.preview_data.forEach(item => {
        if (item.duplicate_info) {
          const duplicateType = item.duplicate_info.duplicate_type;
          // 文件内公司名重复默认为"创建新线索"
          if (duplicateType === 'file_company_name') {
            defaultActions[item.row_num] = 'create_new';
          }
          // 公司名重复默认为"追加手机号"
          else if (duplicateType === 'company_name') {
            defaultActions[item.row_num] = 'add_phone_to_description';
          }
          // 其他默认为"跳过"
          else {
            defaultActions[item.row_num] = 'skip';
          }
        }
      });
      setDuplicateActions(defaultActions);
      
      setCurrent(2);
      
      if (result.is_valid) {
        if (result.duplicate_count > 0) {
          message.warning(`发现${result.duplicate_count}个重复数据，请选择处理方式`);
        } else {
          message.success('数据验证通过');
        }
      } else {
        message.warning(`发现${result.error_count}个错误，请修正后重新上传`);
      }
    } catch (error: any) {
      message.error(error.message || '数据验证失败');
    } finally {
      setLoading(false);
    }
  };

  // 步骤3: 导入数据
  const handleImport = async () => {
    if (!fileList[0] || !validationResult?.is_valid) return;

    setLoading(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append('file', fileList[0].originFileObj as File);
      formData.append('mapping', JSON.stringify(fieldMapping));
      formData.append('duplicate_actions', JSON.stringify(duplicateActions));

      const response = await fetch('/api/v1/leads/import/import', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || '导入失败');
      }

      const result = await response.json();
      setImportResult(result);
      setCurrent(3);
      message.success(result.message);
      
      // 如果全部成功，3秒后自动关闭并重置
      if (result.success_count > 0 && result.skip_count === 0 && result.fail_count === 0) {
        setTimeout(() => {
          setFileList([]);
          setParseResult(null);
          setFieldMapping({});
          setValidationResult(null);
          setImportResult(null);
          setDuplicateActions({});
          setCurrent(0);
        }, 3000);
      }
    } catch (error: any) {
      message.error(error.message || '导入失败');
    } finally {
      setLoading(false);
    }
  };

  // 字段映射表格列
  const mappingColumns: ColumnsType<any> = [
    {
      title: 'Excel列名',
      dataIndex: 'excel_column',
      key: 'excel_column',
      width: 200,
    },
    {
      title: '映射到系统字段',
      key: 'system_field',
      width: 300,
      render: (_: any, record: any) => (
        <Select
          style={{ width: '100%' }}
          value={fieldMapping[record.excel_column]}
          onChange={(value) => {
            setFieldMapping({
              ...fieldMapping,
              [record.excel_column]: value,
            });
          }}
          allowClear
          placeholder="选择系统字段"
        >
          {parseResult && Object.entries(parseResult.system_fields).map(([key, field]) => (
            <Option key={key} value={key}>
              {field.label} {field.required && <Tag color="red">必填</Tag>}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: '预览数据',
      key: 'preview',
      render: (_: any, record: any) => {
        const previewValues = parseResult?.preview_data
          .slice(0, 3)
          .map((row: any) => row[record.excel_column])
          .filter((v: any) => v !== null && v !== undefined);
        return previewValues?.join(', ') || '-';
      },
    },
  ];

  const mappingData = parseResult?.headers.map((header) => ({
    key: header,
    excel_column: header,
  })) || [];

  // 获取导入历史
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const token = getToken();
      const response = await fetch(
        `/api/v1/leads/import/history?skip=${(historyPage - 1) * historyPageSize}&limit=${historyPageSize}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('获取导入历史失败');
      }

      const result = await response.json();
      setHistoryData(result.items);
      setHistoryTotal(result.total);
    } catch (error: any) {
      message.error(error.message || '获取导入历史失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  // 切换到历史标签时加载数据
  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab, historyPage, historyPageSize]);

  // 查看详情
  const handleViewDetail = async (record: ImportHistory) => {
    setDetailVisible(true);
    setDetailLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`/api/v1/leads/import/history/${record.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('获取详情失败');
      }

      const detail = await response.json();
      setCurrentDetail(detail);
    } catch (error: any) {
      message.error(error.message || '获取详情失败');
      setDetailVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  // 下载文件
  const handleDownloadFile = async (record: ImportHistory) => {
    try {
      const token = getToken();
      const response = await fetch(`/api/v1/leads/import/history/${record.id}/download`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('获取下载链接失败');
      }

      const result = await response.json();
      window.open(result.download_url, '_blank');
      message.success('开始下载文件');
    } catch (error: any) {
      message.error(error.message || '下载文件失败');
    }
  };

  // 状态标签
  const getStatusTag = (status: string) => {
    const statusMap: Record<string, { text: string; color: string; icon: React.ReactNode }> = {
      pending: { text: '待处理', color: 'default', icon: <SyncOutlined spin /> },
      processing: { text: '处理中', color: 'processing', icon: <SyncOutlined spin /> },
      success: { text: '成功', color: 'success', icon: <CheckCircleOutlined /> },
      partial_success: { text: '部分成功', color: 'warning', icon: <ExclamationCircleOutlined /> },
      failed: { text: '失败', color: 'error', icon: <CloseCircleOutlined /> },
    };
    const statusInfo = statusMap[status] || { text: status, color: 'default', icon: null };
    return (
      <Tag color={statusInfo.color} icon={statusInfo.icon}>
        {statusInfo.text}
      </Tag>
    );
  };

  // 格式化文件大小
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  // 历史记录表格列
  const historyColumns: ColumnsType<ImportHistory> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '文件名',
      dataIndex: 'original_filename',
      key: 'original_filename',
      width: 200,
      ellipsis: true,
    },
    {
      title: '文件大小',
      dataIndex: 'file_size',
      key: 'file_size',
      width: 100,
      render: (size: number) => formatFileSize(size),
    },
    {
      title: '导入人',
      dataIndex: 'importer_name',
      key: 'importer_name',
      width: 100,
    },
    {
      title: '导入时间',
      dataIndex: 'import_time',
      key: 'import_time',
      width: 180,
      render: (time: string) => dayjs.utc(time).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: '总行数',
      dataIndex: 'total_rows',
      key: 'total_rows',
      width: 100,
    },
    {
      title: '成功',
      dataIndex: 'success_count',
      key: 'success_count',
      width: 80,
      render: (count: number) => <Text type="success">{count}</Text>,
    },
    {
      title: '跳过',
      dataIndex: 'skip_count',
      key: 'skip_count',
      width: 80,
      render: (count: number) => (count > 0 ? <Text type="warning">{count}</Text> : count),
    },
    {
      title: '失败',
      dataIndex: 'fail_count',
      key: 'fail_count',
      width: 80,
      render: (count: number) => (count > 0 ? <Text type="danger">{count}</Text> : count),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_: any, record: ImportHistory) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            详情
          </Button>
          {record.has_file && (
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleDownloadFile(record)}
            >
              下载
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Title level={3}>线索批量导入</Title>
        <Paragraph type="secondary">
          支持从Excel文件批量导入线索数据，导入的线索将自动进入公海池
        </Paragraph>

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane tab="导入线索" key="import" icon={<UploadOutlined />}>
            <div style={{ marginTop: 16 }}>

        <Steps current={current} style={{ marginBottom: 32 }}>
          <Step title="上传文件" description="上传Excel文件" />
          <Step title="字段映射" description="配置字段对应关系" />
          <Step title="数据验证" description="验证数据格式" />
          <Step title="导入结果" description="查看导入结果" />
        </Steps>

        {/* 步骤0: 上传文件 */}
        {current === 0 && (
          <div>
            <Alert
              message="导入说明"
              description={
                <div>
                  <p>1. 支持 .xlsx 和 .xls 格式的Excel文件</p>
                  <p>2. 必填字段：联系人姓名、手机号</p>
                  <p>3. 手机号必须是11位中国大陆手机号</p>
                  <p>4. 数据来源、线索类型、跟进意向等字段需符合系统枚举值</p>
                  <p>5. 导入的线索将自动进入公海池</p>
                </div>
              }
              type="info"
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 24 }}
            />

            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽Excel文件到此区域上传</p>
              <p className="ant-upload-hint">
                支持 .xlsx 和 .xls 格式
              </p>
            </Upload.Dragger>

            <Divider />

            <Button
              icon={<DownloadOutlined />}
              onClick={async () => {
                try {
                  const token = getToken();
                  const response = await fetch('/api/v1/leads/import/template', {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                    },
                  });
                  
                  if (!response.ok) {
                    throw new Error('下载失败');
                  }
                  
                  const blob = await response.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'lead_import_template.xlsx';
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                  message.success('模板下载成功');
                } catch (error) {
                  message.error('模板下载失败');
                }
              }}
            >
              下载导入模板
            </Button>
          </div>
        )}

        {/* 步骤1: 字段映射 */}
        {current === 1 && parseResult && (
          <div>
            <Alert
              message={`共识别到 ${parseResult.total_rows} 行数据，${parseResult.headers.length} 个列`}
              type="success"
              style={{ marginBottom: 16 }}
            />

            <Title level={5}>配置字段映射</Title>
            <Paragraph type="secondary">
              系统已自动识别部分字段，请检查并调整映射关系
            </Paragraph>

            <Table
              columns={mappingColumns}
              dataSource={mappingData}
              pagination={false}
              size="small"
              style={{ marginBottom: 24 }}
            />

            <Space>
              <Button onClick={() => setCurrent(0)}>上一步</Button>
              <Button
                type="primary"
                onClick={handleValidate}
                loading={loading}
                disabled={
                  !Object.values(fieldMapping).includes('contact_name') || 
                  !Object.values(fieldMapping).includes('phone')
                }
              >
                下一步：验证数据
              </Button>
            </Space>
          </div>
        )}

        {/* 步骤2: 数据验证 */}
        {current === 2 && validationResult && (
          <div>
            {validationResult.is_valid ? (
              <Alert
                message="数据验证通过"
                description={
                  <div>
                    <p>共 {validationResult.total_rows} 行数据</p>
                    {validationResult.duplicate_count > 0 && (
                      <>
                        <p style={{ color: '#faad14' }}>
                          发现 {validationResult.duplicate_count} 个重复数据
                          {validationResult.company_duplicate_count > 0 && 
                            `（其中 ${validationResult.company_duplicate_count} 个为公司类型）`
                          }
                        </p>
                        <p style={{ fontSize: '12px', color: '#666' }}>
                          请在下方为每个重复数据选择处理方式
                        </p>
                      </>
                    )}
                  </div>
                }
                type={validationResult.duplicate_count > 0 ? 'warning' : 'success'}
                icon={validationResult.duplicate_count > 0 ? <ExclamationCircleOutlined /> : <CheckCircleOutlined />}
                style={{ marginBottom: 24 }}
              />
            ) : (
              <Alert
                message="数据验证失败"
                description={`共 ${validationResult.total_rows} 行数据，发现 ${validationResult.error_count} 个错误`}
                type="error"
                icon={<CloseCircleOutlined />}
                style={{ marginBottom: 24 }}
              />
            )}

            {!validationResult.is_valid && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>错误列表（最多显示100条）</Title>
                <List
                  size="small"
                  bordered
                  dataSource={validationResult.errors}
                  renderItem={(error) => (
                    <List.Item>
                      <Text type="danger">{error}</Text>
                    </List.Item>
                  )}
                  style={{ maxHeight: 300, overflow: 'auto' }}
                />
              </div>
            )}

            {/* 重复数据处理选择 */}
            {validationResult.is_valid && validationResult.duplicate_count > 0 && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>重复数据处理（共{validationResult.duplicate_count}条）</Title>
                <Paragraph type="secondary">
                  以下数据的手机号存在重复，请为每条数据选择处理方式
                </Paragraph>

                <Table
                  size="small"
                  dataSource={validationResult.preview_data.filter(item => item.duplicate_info)}
                  pagination={false}
                  rowKey="row_num"
                  rowClassName={(record) =>
                    record.duplicate_info?.duplicate_type === 'file' ? 'file-duplicate-row' : ''
                  }
                  columns={[
                    {
                      title: 'Excel行号',
                      dataIndex: 'row_num',
                      key: 'row_num',
                      width: 80,
                    },
                    {
                      title: '重复类型',
                      key: 'duplicate_type',
                      width: 100,
                      render: (_, record) => {
                        const duplicateType = record.duplicate_info?.duplicate_type;
                        const typeConfig: Record<string, { text: string; color: string }> = {
                          'file': { text: '文件内重复', color: 'orange' },
                          'database': { text: '数据库重复', color: 'blue' },
                          'company_name': { text: '公司重复', color: 'purple' },
                          'file_company_name': { text: '文件内公司重复', color: 'cyan' },
                        };
                        const config = duplicateType ? (typeConfig[duplicateType] || { text: '重复', color: 'default' }) : { text: '重复', color: 'default' };
                        return (
                          <Tag color={config.color}>
                            {config.text}
                          </Tag>
                        );
                      },
                    },
                    {
                      title: '手机号',
                      key: 'phone',
                      width: 120,
                      render: (_, record) => record.duplicate_info?.phone,
                    },
                    {
                      title: 'Excel中的联系人',
                      key: 'new_contact_name',
                      width: 130,
                      render: (_, record) => (
                        <div>
                          <div>{record.duplicate_info?.new_contact_name}</div>
                          {record.duplicate_info?.is_company && (
                            <Tag color="blue" style={{ marginTop: 4 }}>公司</Tag>
                          )}
                        </div>
                      ),
                    },
                    {
                      title: '首次出现行号',
                      key: 'first_occurrence_row',
                      width: 100,
                      render: (_, record) => {
                        if (record.duplicate_info?.duplicate_type === 'file') {
                          return <Tag color="orange">第{record.duplicate_info?.first_occurrence_row}行</Tag>;
                        }
                        return <Text type="secondary">-</Text>;
                      },
                    },
                    {
                      title: '系统中的联系人',
                      key: 'existing_contact_name',
                      width: 120,
                      render: (_, record) => {
                        if (record.duplicate_info?.duplicate_type === 'file') {
                          return <Text type="secondary">-</Text>;
                        }
                        return record.duplicate_info?.existing_contact_name;
                      },
                    },
                    {
                      title: '系统中的状态',
                      key: 'existing_status',
                      width: 100,
                      render: (_, record) => {
                        if (record.duplicate_info?.duplicate_type === 'file') {
                          return <Text type="secondary">-</Text>;
                        }
                        const status = record.duplicate_info?.existing_status;
                        const statusMap: Record<string, { text: string; color: string }> = {
                          'PUBLIC_POOL': { text: '公海池', color: 'default' },
                          'ASSIGNED': { text: '已分配', color: 'blue' },
                          'CONVERTED': { text: '已转化', color: 'green' },
                          'RECYCLED': { text: '已回收', color: 'orange' },
                        };
                        const statusInfo = status ? (statusMap[status] || { text: status, color: 'default' }) : { text: '-', color: 'default' };
                        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
                      },
                    },
                    {
                      title: '最近跟进时间',
                      key: 'existing_last_follow_time',
                      width: 140,
                      render: (_, record) => {
                        if (record.duplicate_info?.duplicate_type === 'file' || record.duplicate_info?.duplicate_type === 'file_company_name') {
                          return <Text type="secondary">-</Text>;
                        }
                        const time = record.duplicate_info?.existing_last_follow_time;
                        if (!time) return <Text type="secondary">未跟进</Text>;
                        return dayjs(time).format('YYYY-MM-DD HH:mm');
                      },
                    },
                    {
                      title: '该公司所有手机号',
                      key: 'all_phones',
                      width: 180,
                      render: (_, record) => {
                        // 只对文件内公司名重复显示
                        if (record.duplicate_info?.duplicate_type !== 'file_company_name') {
                          return <Text type="secondary">-</Text>;
                        }
                        const allPhones = record.duplicate_info?.all_phones || [];
                        const currentPhone = record.duplicate_info?.phone;
                        return (
                          <div>
                            {allPhones.map((phone: string) => (
                              <Tag key={phone} color={phone === currentPhone ? 'green' : 'default'} style={{ marginBottom: 2 }}>
                                {phone}{phone === currentPhone ? ' (当前)' : ''}
                              </Tag>
                            ))}
                          </div>
                        );
                      },
                    },
                    {
                      title: '处理方式',
                      key: 'action',
                      width: 200,
                      render: (_, record) => {
                        const duplicateType = record.duplicate_info?.duplicate_type;
                        const isFileDuplicate = duplicateType === 'file';
                        const isCompanyDuplicate = duplicateType === 'company_name';
                        const isFileCompanyDuplicate = duplicateType === 'file_company_name';

                        return (
                          <Select
                            style={{ width: '100%' }}
                            value={duplicateActions[record.row_num] || (isFileCompanyDuplicate ? 'create_new' : 'skip')}
                            onChange={(value) => {
                              setDuplicateActions({
                                ...duplicateActions,
                                [record.row_num]: value,
                              });
                            }}
                            disabled={isFileCompanyDuplicate}  // 文件内公司名重复不可选择，固定为创建新线索
                          >
                            {isFileCompanyDuplicate ? (
                              // 文件内公司名重复：只有创建新线索一个选项
                              <Option value="create_new">
                                <span style={{ color: '#52c41a' }}>创建新线索（自动补充备注）</span>
                              </Option>
                            ) : isFileDuplicate ? (
                              // 文件内手机号重复：只能跳过或创建新线索
                              <>
                                <Option value="skip">
                                  <span style={{ color: '#999' }}>跳过</span>
                                </Option>
                                <Option value="create_new">
                                  <span style={{ color: '#52c41a' }}>创建新线索</span>
                                </Option>
                              </>
                            ) : isCompanyDuplicate ? (
                              // 数据库公司名重复：可以追加手机号到备注
                              <>
                                <Option value="skip">
                                  <span style={{ color: '#999' }}>跳过</span>
                                </Option>
                                <Option value="add_phone_to_description">
                                  <span style={{ color: '#722ed1' }}>追加手机号到备注</span>
                                </Option>
                                <Option value="create_new">
                                  <span style={{ color: '#52c41a' }}>创建新线索</span>
                                </Option>
                              </>
                            ) : (
                              // 数据库重复（手机号）可以添加跟进记录
                              <>
                                <Option value="skip">
                                  <span style={{ color: '#999' }}>跳过</span>
                                </Option>
                                <Option value="add_follow">
                                  <span style={{ color: '#1890ff' }}>添加跟进记录</span>
                                </Option>
                                <Option value="create_new">
                                  <span style={{ color: '#52c41a' }}>创建新线索</span>
                                </Option>
                              </>
                            )}
                          </Select>
                        );
                      },
                    },
                  ]}
                />

                <Alert
                  message="处理方式说明"
                  description={
                    <div style={{ fontSize: '12px' }}>
                      <p><strong>跳过：</strong>不导入该条数据，不对现有线索做任何操作</p>
                      <p><strong>添加跟进记录：</strong>不创建新线索，为现有线索添加一条"导入线索"的跟进记录（手机号重复时可用）</p>
                      <p><strong>追加手机号到备注：</strong>将新手机号追加到已有线索的备注中，并添加跟进记录（公司名重复时可用）</p>
                      <p><strong>创建新线索：</strong>忽略重复，创建新的线索</p>
                      <p><strong>创建新线索（自动补充备注）：</strong>创建新线索，并在备注中记录该公司其他手机号（文件内公司名重复时自动执行）</p>
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                />
              </div>
            )}

            <Space>
              <Button onClick={() => setCurrent(1)}>上一步</Button>
              {validationResult.is_valid ? (
                <Button
                  type="primary"
                  onClick={handleImport}
                  loading={loading}
                >
                  开始导入
                </Button>
              ) : (
                <Button onClick={() => setCurrent(0)}>
                  修正数据后重新上传
                </Button>
              )}
            </Space>
          </div>
        )}

        {/* 步骤3: 导入结果 */}
        {current === 3 && importResult && (
          <div>
            <Alert
              message="导入完成"
              description={
                <div>
                  <p>总行数：{importResult.total_rows}</p>
                  <p>成功导入：{importResult.success_count} 条</p>
                  {importResult.skip_count > 0 && (
                    <p style={{ color: '#faad14' }}>重复跳过：{importResult.skip_count} 条</p>
                  )}
                  {importResult.fail_count > 0 && (
                    <p style={{ color: '#ff4d4f' }}>导入失败：{importResult.fail_count} 条</p>
                  )}
                </div>
              }
              type={importResult.fail_count > 0 ? 'warning' : 'success'}
              icon={<CheckCircleOutlined />}
              style={{ marginBottom: 24 }}
            />

            {/* 重复数据列表 */}
            {importResult.duplicate_data && importResult.duplicate_data.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>重复数据列表（共{importResult.duplicate_data.length}条）</Title>
                <Table
                  size="small"
                  dataSource={importResult.duplicate_data}
                  pagination={{ pageSize: 10 }}
                  columns={[
                    {
                      title: 'Excel行号',
                      dataIndex: 'row_num',
                      key: 'row_num',
                      width: 100,
                    },
                    {
                      title: '手机号',
                      dataIndex: 'phone',
                      key: 'phone',
                      width: 120,
                    },
                    {
                      title: 'Excel中的姓名',
                      dataIndex: 'contact_name',
                      key: 'contact_name',
                      width: 120,
                    },
                    {
                      title: '系统中的姓名',
                      dataIndex: 'existing_contact_name',
                      key: 'existing_contact_name',
                      width: 120,
                    },
                    {
                      title: '系统中的状态',
                      dataIndex: 'existing_status',
                      key: 'existing_status',
                      width: 120,
                      render: (status: string) => {
                        const statusMap: Record<string, { text: string; color: string }> = {
                          'PUBLIC_POOL': { text: '公海池', color: 'default' },
                          'ASSIGNED': { text: '已分配', color: 'blue' },
                          'CONVERTED': { text: '已转化', color: 'green' },
                          'RECYCLED': { text: '已回收', color: 'orange' },
                        };
                        const statusInfo = statusMap[status] || { text: status, color: 'default' };
                        return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
                      },
                    },
                  ]}
                />
              </div>
            )}

            {/* 失败数据列表 */}
            {importResult.errors && importResult.errors.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>失败数据列表</Title>
                <List
                  size="small"
                  bordered
                  dataSource={importResult.errors}
                  renderItem={(error) => (
                    <List.Item>
                      <Text type="danger">{error}</Text>
                    </List.Item>
                  )}
                  style={{ maxHeight: 300, overflow: 'auto' }}
                />
              </div>
            )}

            <Space>
              <Button
                type="primary"
                onClick={() => {
                  setFileList([]);
                  setParseResult(null);
                  setFieldMapping({});
                  setValidationResult(null);
                  setImportResult(null);
                  setCurrent(0);
                  // 切换到历史标签页
                  setActiveTab('history');
                }}
              >
                完成并查看历史
              </Button>
              <Button
                onClick={() => {
                  setFileList([]);
                  setParseResult(null);
                  setFieldMapping({});
                  setValidationResult(null);
                  setImportResult(null);
                  setDuplicateActions({});
                  setCurrent(0);
                }}
              >
                继续导入
              </Button>
            </Space>
          </div>
        )}
            </div>
          </Tabs.TabPane>

          <Tabs.TabPane tab="导入历史" key="history" icon={<HistoryOutlined />}>
            <Table
              columns={historyColumns}
              dataSource={historyData}
              rowKey="id"
              loading={historyLoading}
              pagination={{
                current: historyPage,
                pageSize: historyPageSize,
                total: historyTotal,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                onChange: (page, pageSize) => {
                  setHistoryPage(page);
                  setHistoryPageSize(pageSize);
                },
              }}
              scroll={{ x: 1400 }}
            />
          </Tabs.TabPane>
        </Tabs>

        {/* 详情弹窗 */}
        <Modal
          title="导入详情"
          open={detailVisible}
          onCancel={() => setDetailVisible(false)}
          footer={null}
          width={900}
        >
          {detailLoading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : currentDetail ? (
            <div>
              <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="文件名" span={2}>
                  {currentDetail.original_filename}
                </Descriptions.Item>
                <Descriptions.Item label="文件大小">
                  {formatFileSize(currentDetail.file_size)}
                </Descriptions.Item>
                <Descriptions.Item label="导入人">
                  {currentDetail.importer_name}
                </Descriptions.Item>
                <Descriptions.Item label="导入时间">
                  {dayjs.utc(currentDetail.import_time).tz('Asia/Shanghai').format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label="状态">
                  {getStatusTag(currentDetail.status)}
                </Descriptions.Item>
                <Descriptions.Item label="总行数">
                  {currentDetail.total_rows}
                </Descriptions.Item>
                <Descriptions.Item label="成功数量">
                  <Text type="success">{currentDetail.success_count}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="跳过数量">
                  <Text type="warning">{currentDetail.skip_count}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="失败数量">
                  <Text type="danger">{currentDetail.fail_count}</Text>
                </Descriptions.Item>
              </Descriptions>

              {/* 字段映射 */}
              {currentDetail.field_mapping && (
                <div style={{ marginTop: 24 }}>
                  <Title level={5}>字段映射</Title>
                  <Descriptions bordered column={1} size="small">
                    {Object.entries(currentDetail.field_mapping).map(([key, value]) => (
                      <Descriptions.Item key={key} label={key}>
                        {value}
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                </div>
              )}

              {/* 重复数据 */}
              {currentDetail.duplicate_details && currentDetail.duplicate_details.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <Title level={5}>重复数据（共{currentDetail.duplicate_details.length}条）</Title>
                  <Table
                    size="small"
                    dataSource={currentDetail.duplicate_details}
                    pagination={{ pageSize: 5 }}
                    columns={[
                      { title: 'Excel行号', dataIndex: 'row_num', key: 'row_num', width: 100 },
                      { title: '手机号', dataIndex: 'phone', key: 'phone', width: 120 },
                      { title: 'Excel姓名', dataIndex: 'contact_name', key: 'contact_name', width: 120 },
                      { title: '系统姓名', dataIndex: 'existing_contact_name', key: 'existing_contact_name', width: 120 },
                    ]}
                  />
                </div>
              )}

              {/* 错误详情 */}
              {currentDetail.error_details && currentDetail.error_details.length > 0 && (
                <div style={{ marginTop: 24 }}>
                  <Title level={5}>错误详情（共{currentDetail.error_details.length}条）</Title>
                  <List
                    size="small"
                    bordered
                    dataSource={currentDetail.error_details}
                    renderItem={(error) => (
                      <List.Item>
                        <Text type="danger">{error}</Text>
                      </List.Item>
                    )}
                    style={{ maxHeight: 300, overflow: 'auto' }}
                  />
                </div>
              )}
            </div>
          ) : null}
        </Modal>
      </Card>
    </div>
  );
};

export default LeadImport;
